import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Card } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import { Button } from '@shared/ui/Button';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

export const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

export const TitleBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const Subtitle = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const ActionsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  max-width: 100%;
`;

// =============================================================================
// 1. Интерактивный конвейер Реестра платежей (5 ступеней со стрелками)
// =============================================================================

export const AnalyticsHeroWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const PipelineWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
  align-items: stretch;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

export const StageCard = styled(Card)<{ $active: boolean; $tone?: string }>`
  padding: 14px 16px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
  transition: all 0.2s ease;
  border-width: 2px;
  border-color: ${({ $active, $tone }) =>
    $active ? ($tone ? $tone : '#10b981') : 'transparent'};
  background: ${({ $active, theme }) =>
    $active ? `${theme.colors.backgroundElevated}` : theme.colors.surface};

  &:hover {
    transform: translateY(-2px);
    border-color: ${({ $tone }) => ($tone ? $tone : '#10b981')};
  }
`;

export const StageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const StageBadge = styled.span<{ $tone?: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  font-size: 11px;
  font-weight: 800;
  background: ${({ $tone }) => ($tone ? `${$tone}22` : '#10b98122')};
  color: ${({ $tone }) => ($tone ? $tone : '#10b981')};
  border: 1px solid ${({ $tone }) => ($tone ? `${$tone}66` : '#10b98166')};
`;

export const StageTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  flex: 1;
`;

export const StageMetrics = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

export const MetricCount = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const MetricAmount = styled.div`
  font-size: 16px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const FactSubGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 11px;
`;

export const FactRow = styled.div<{ $color?: string }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${({ $color, theme }) => ($color ? $color : theme.colors.text.secondary)};
  font-weight: 600;
`;

// =============================================================================
// 2. Балансировщик Ликвидности (Cash Thermometer)
// =============================================================================

export const ThermometerCard = styled(Card)`
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  width: 100%;
  min-width: 0;
  box-sizing: border-box;

  @media (max-width: 480px) {
    padding: 12px 14px;
  }
`;

export const ThermometerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
`;

export const ThermometerTitle = styled.div`
  font-size: 14px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ThermometerStats = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  font-size: 12px;
`;

export const StatItem = styled.div`
  display: flex;
  align-items: baseline;
  gap: 5px;
`;

export const StatLabel = styled.span`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const StatVal = styled.span<{ $tone?: string }>`
  font-weight: 800;
  font-size: 14px;
  color: ${({ $tone, theme }) => {
    if (!$tone) return theme.colors.text.primary;
    const isDark = theme.name === 'dark';
    if ($tone === '#15803d') return isDark ? theme.colors.status.success : '#15803d';
    if ($tone === '#0369a1') return isDark ? '#38bdf8' : '#0369a1';
    if ($tone === '#b91c1c') return isDark ? '#f87171' : '#b91c1c';
    return $tone;
  }};
`;

export const RetryMiniButton = styled(Button)`
  padding: 0 4px;
  font-size: 11px;
`;

export const ProgressTrack = styled.div`
  width: 100%;
  height: 12px;
  border-radius: 6px;
  background: #334155;
  overflow: hidden;
  display: flex;
  position: relative;
`;

export const Segment = styled.div<{ $width: number; $color: string }>`
  width: ${({ $width }) => Math.min(100, Math.max(0, $width))}%;
  height: 100%;
  background: ${({ $color }) => $color};
  transition: width 0.3s ease;
`;

/** Подписи шкалы: что значит каждый цвет, и дефицит — отдельно от шкалы. */
export const ScaleLegend = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 16px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const ScaleLegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

export const ScaleSwatch = styled.span<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${({ $color }) => $color};
`;

export const DeficitMark = styled.span`
  padding: 1px 8px;
  border-radius: ${({ theme }) => theme.radius.pill};
  border: 1px solid #ef4444;
  background: rgba(239, 68, 68, 0.12);
  font-weight: 700;
  color: ${({ theme }) => (theme.name === 'dark' ? '#f87171' : '#b91c1c')};
`;

export const DeficitBanner = styled.div`
  padding: 10px 14px;
  border-radius: 8px;
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid #ef4444;
  color: ${({ theme }) => (theme.name === 'dark' ? '#f87171' : '#b91c1c')};
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: flex-start;
  gap: 8px;

  & > svg {
    flex-shrink: 0;
  }
`;

// =============================================================================
// 3. Панель фильтров и выбора активного Реестра
// =============================================================================

export const FilterBar = styled(Card)`
  padding: 14px 18px;
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  width: 100%;
  min-width: 0;
  box-sizing: border-box;

  @media (max-width: 480px) {
    padding: 10px 12px;
  }
`;

export const RegistrySelectBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const RegistrySelectLabel = styled.span`
  font-size: 13px;
  font-weight: 700;
`;

export const RegistrySelect = styled.select`
  padding: 8px 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
  font-weight: 700;
  min-width: 240px;
  max-width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #10b981;
  }
`;

export const SearchInput = styled.input`
  padding: 8px 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
  min-width: 0;
  width: 100%;
  flex: 1 1 200px;
  max-width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #10b981;
  }
`;

export const Select = styled.select`
  padding: 8px 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
  max-width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #10b981;
  }
`;

/** Таблица заявок прокручивается внутри себя: шапка закреплена именно в этой области, и у
 *  длинного списка обе полосы прокрутки видны в окне, а не под последней строкой. */
export const TableWrap = styled(Card)`
  padding: 0;
  max-height: min(70vh, calc(100vh - 260px));
  overflow: auto;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  -webkit-overflow-scrolling: touch;

  /* Шапка — над закреплёнными ячейками строк (получатель и действия, z-index 2). */
  thead th {
    z-index: 3;
  }
`;

export const CheckboxTh = styled.th`
  width: 36px;
`;

export const RightAlignedTh = styled.th`
  min-width: 200px;
  text-align: right;
`;

export const RightAlignedSimpleTh = styled.th`
  text-align: right;
`;

export const TableNoticeTd = styled.td`
  text-align: center;
  padding: 24px;
`;

export const StickyRecipientTh = styled.th`
  position: sticky;
  left: 0;
  background: ${({ theme }) => theme.colors.surface};
  z-index: 3;
  box-shadow: 2px 0 6px rgba(0, 0, 0, 0.08);
  min-width: 240px;

  /* Угол шапки закреплён и сверху, и слева — над остальной шапкой при прокрутке вбок. */
  && {
    z-index: 4;
  }
`;

export const StickyRecipientTd = styled.td<{ $selected?: boolean }>`
  position: sticky;
  left: 0;
  background: ${({ $selected, theme }) =>
    $selected ? 'rgba(16, 185, 129, 0.12)' : theme.colors.surface};
  z-index: 2;
  box-shadow: 2px 0 6px rgba(0, 0, 0, 0.08);
  min-width: 240px;
`;

export const StickyTh = styled.th`
  position: sticky;
  right: 0;
  background: ${({ theme }) => theme.colors.surface};
  z-index: 2;
  box-shadow: -4px 0 8px rgba(0, 0, 0, 0.08);
  text-align: center;

  && {
    z-index: 4;
  }
`;

export const StickyTd = styled.td`
  position: sticky;
  right: 0;
  background: ${({ theme }) => theme.colors.surface};
  z-index: 2;
  box-shadow: -4px 0 8px rgba(0, 0, 0, 0.08);
  text-align: center;
`;

export const RequestRowTr = styled.tr<{ $selected?: boolean }>`
  background: ${({ $selected }) => ($selected ? 'rgba(16, 185, 129, 0.08)' : 'transparent')};
`;

export const RequestNumberText = styled.div`
  font-weight: 700;
`;

export const DueDateText = styled.div`
  font-size: 11px;
  opacity: 0.7;
`;

export const DocRefText = styled.div`
  font-size: 11px;
  opacity: 0.75;
  margin-top: 3px;
`;

/** Заявка сотрудника: кто подал и из какого отдела. */
export const StaffInfoText = styled.div`
  font-size: 11px;
  margin-top: 3px;
  max-width: 180px;
`;

export const StaffCardLink = styled(Link)`
  display: inline-block;
  margin-top: 3px;
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
`;

export const RecipientHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const RecipientName = styled.div`
  font-weight: 600;
`;

export const DemoBadge = styled(Badge)`
  font-size: 10px;
`;

export const RequisitesText = styled.div`
  font-size: 11px;
  opacity: 0.75;
`;

export const AccountText = styled.div`
  font-size: 11px;
  opacity: 0.7;
`;

export const PurposeText = styled.div`
  font-size: 12px;
  opacity: 0.85;
  margin-top: 2px;
  max-width: 280px;
`;

export const GatesStack = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

export const GateChip = styled.span<{ $tone: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 800;
  cursor: pointer;
  user-select: none;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 1px;
  }

  ${({ $tone, theme }) => {
    const isDark = theme.name === 'dark';
    switch ($tone) {
      case 'success':
        return `background: ${theme.colors.status.success}22; color: ${isDark ? theme.colors.status.success : '#166534'}; border: 1px solid ${isDark ? theme.colors.status.success + '55' : '#16653455'};`;
      case 'warning':
        return `background: ${theme.colors.status.warning}22; color: ${isDark ? theme.colors.status.warning : '#9a3412'}; border: 1px solid ${isDark ? theme.colors.status.warning + '55' : '#9a341255'};`;
      case 'danger':
        return `background: ${theme.colors.status.danger}22; color: ${isDark ? '#f87171' : '#b91c1c'}; border: 1px solid ${isDark ? '#f8717155' : '#b91c1c55'};`;
      case 'info':
        return `background: #0ea5e922; color: ${isDark ? '#38bdf8' : '#075985'}; border: 1px solid ${isDark ? '#38bdf855' : '#07598555'};`;
      default:
        return `background: ${theme.colors.surface}; color: ${theme.colors.text.secondary}; border: 1px solid ${theme.colors.border};`;
    }
  }}
`;

export const TabularRightTd = styled.td`
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

export const CompactSummaryBar = styled(Card)`
  padding: 8px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 13px;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  border-left: 4px solid #10b981;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
`;

export const CompactSummaryText = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 12px;
`;

export const CompactMetric = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

export const InlineLabel = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const AlertBanner = styled.div<{ $tone: 'success' | 'info' | 'warning' }>`
  padding: 10px 16px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  white-space: pre-line;
  display: flex;
  align-items: center;
  justify-content: space-between;

  ${({ $tone, theme }) => {
    const isDark = theme.name === 'dark';
    switch ($tone) {
      case 'success':
        return `background: ${theme.colors.status.success}22; color: ${isDark ? theme.colors.status.success : '#166534'}; border: 1px solid ${isDark ? theme.colors.status.success + '66' : '#16653466'};`;
      case 'warning':
        return `background: ${theme.colors.status.warning}22; color: ${isDark ? theme.colors.status.warning : '#9a3412'}; border: 1px solid ${isDark ? theme.colors.status.warning + '66' : '#9a341266'};`;
      default:
        return `background: #0ea5e922; color: ${isDark ? '#38bdf8' : '#075985'}; border: 1px solid ${isDark ? '#38bdf866' : '#07598566'};`;
    }
  }}
`;

// =============================================================================
// Стили для инлайн-утверждения сумм и факта оплаты
// =============================================================================

export const InlineApprovalBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-end;
`;

export const InlineInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const SmallInput = styled.input`
  width: 110px;
  padding: 4px 6px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 12px;
  font-weight: 700;
  text-align: right;

  &:focus {
    outline: none;
    border-color: #10b981;
  }

  &[aria-invalid='true'] {
    border-color: ${({ theme }) => (theme.name === 'dark' ? '#f87171' : '#b91c1c')};
  }
`;

export const InlineAmountError = styled.div`
  max-width: 220px;
  font-size: 10px;
  font-weight: 600;
  text-align: right;
  color: ${({ theme }) => (theme.name === 'dark' ? '#f87171' : '#b91c1c')};
`;

export const MiniBtn = styled.button`
  padding: 2px 5px;
  border-radius: 3px;
  border: 1px solid #334155;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;

  &:hover {
    border-color: #10b981;
    color: #10b981;
  }
`;

export const RowActionButton = styled(Button)`
  width: 32px;
  padding: 0;
`;

export const CarryoverBadge = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: ${({ theme }) => (theme.name === 'dark' ? theme.colors.status.warning : '#9a3412')};
  background: ${({ theme }) => theme.colors.status.warning}18;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.status.warning}44;
  margin-top: 2px;
  display: inline-block;

  & > svg {
    vertical-align: -2px;
    margin-right: 4px;
  }
`;

export const ExecInfoRow = styled.div`
  font-size: 10px;
  color: ${({ theme }) => theme.colors.text.secondary};
  display: flex;
  justify-content: space-between;
`;

export const ExecutionCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 130px;
`;

export const ExecutionCellHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
`;

export const MarkPaidBtn = styled(Button)`
  font-size: 10px;
  padding: 1px 5px;
`;

export const PriorityBadgeWrap = styled.div`
  margin-top: 3px;
`;

export const RowActionsWrap = styled.div`
  display: flex;
  gap: 4px;
  justify-content: center;
`;

export const MiniProgressBar = styled.div`
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: #334155;
  overflow: hidden;
`;

export const MiniProgressFill = styled.div<{ $pct: number }>`
  width: ${({ $pct }) => Math.min(100, Math.max(0, $pct))}%;
  height: 100%;
  background: #10b981;
`;

export const MonoCode = styled.span`
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 11px;
`;

export const RegNumberText = styled.div`
  font-size: 10px;
  margin-top: 2px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#38bdf8' : '#0369a1')};
`;

export const GateNotesText = styled.div`
  font-size: 10px;
  margin-top: 3px;
  max-width: 140px;
  color: ${({ theme }) => (theme.name === 'dark' ? theme.colors.status.warning : '#b45309')};
`;

export const ApprovedAmountText = styled.div`
  font-weight: 800;
  font-size: 14px;
  color: ${({ theme }) => (theme.name === 'dark' ? theme.colors.status.success : '#15803d')};
`;

export const CarryoverRefText = styled.div`
  font-size: 10px;
  color: ${({ theme }) => (theme.name === 'dark' ? '#38bdf8' : '#0369a1')};

  & > svg {
    vertical-align: -2px;
    margin-right: 4px;
  }
`;

export const BankDocText = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'dark' ? theme.colors.status.success : '#15803d')};
`;

export const LiquidityErrorText = styled.span`
  color: ${({ theme }) => (theme.name === 'dark' ? '#f87171' : '#b91c1c')};
  display: flex;
  align-items: center;
  gap: 6px;
`;
